import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, message } from 'antd';
import { ArrowRightOutlined, FileTextOutlined } from '@ant-design/icons';
import { financeSettingsApi } from '../../utils/api';
import EstimateForm from '../../components/finance/EstimateForm';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { getDefaultCurrencyId } from '../../utils/currencyUtils';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { mapItemDiscountFields } from '../../utils/itemDiscountUtils';

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  cost_per_item: 0,
  discount: 0,
  discount_type: 'percent',
  taxes: [],
  amount: 0,
  item_summary: '',
});

const AddEstimate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cloneEstimateId = searchParams.get('estimate');
  const presetProjectId = searchParams.get('project_id');
  const presetClientId = searchParams.get('default_client') || searchParams.get('client_id');
  const presetDealId = searchParams.get('deal_id');
  const returnTo = location.state?.returnTo;
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [lockClient, setLockClient] = useState(false);
  const [lockProject, setLockProject] = useState(false);
  const [options, setOptions] = useState({
    currencies: [],
    clients: [],
    projects: [],
    bankAccounts: [],
    categories: [],
    products: [],
    units: [],
    taxes: [],
  });
  const [initialValues, setInitialValues] = useState(null);
  const [initialItems, setInitialItems] = useState([createEmptyItem()]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const createParams = {};
        if (cloneEstimateId) createParams.estimate = cloneEstimateId;
        if (presetProjectId) createParams.project_id = presetProjectId;
        if (presetClientId) createParams.default_client = presetClientId;

        const res = await financeSettingsApi.getEstimateCreateData(createParams);
        const data = res?.data || {};
        const sourceEstimate = data.estimate || null;
        const selectedProject = data.selected_project;
        const selectedClient = data.selected_client;
        const last = data.last_estimate_number || '000';
        const nextNumber = String(Number(last) + 1).padStart(Math.max(3, String(last).length), '0');

        const clients = data.clients || [];
        const projects = data.projects || [];
        if (
          selectedClient?.id != null &&
          !clients.some((c) => String(c.id) === String(selectedClient.id))
        ) {
          clients.push(selectedClient);
        }
        if (
          selectedProject?.id != null &&
          !projects.some((p) => String(p.id) === String(selectedProject.id))
        ) {
          projects.push({
            id: selectedProject.id,
            project_name: selectedProject.project_name,
          });
        }

        setOptions({
          currencies: data.currencies || [],
          clients,
          projects,
          bankAccounts: data.bankAccounts || [],
          categories: data.categories || [],
          products: data.products || [],
          units: data.units || [],
          taxes: data.taxes || [],
        });

        const resolvedProjectId =
          selectedProject?.id ?? (presetProjectId != null ? Number(presetProjectId) : undefined);
        const resolvedClientId =
          selectedClient?.id ??
          sourceEstimate?.client_id ??
          (presetClientId != null ? Number(presetClientId) : undefined);

        setLockProject(Boolean(resolvedProjectId && (presetProjectId || selectedProject)));
        setLockClient(Boolean(resolvedClientId && (presetClientId || selectedClient)));

        setInitialValues({
          estimate_number: `EST#${nextNumber}`,
          valid_till: formatJalaliApiDate(sourceEstimate?.valid_till),
          currency_id: sourceEstimate?.currency_id || getDefaultCurrencyId(data.currencies),
          client_id: resolvedClientId,
          project_id: resolvedProjectId,
          deal_id: presetDealId != null ? Number(presetDealId) : sourceEstimate?.deal_id || undefined,
          bank_account_id: sourceEstimate?.bank_account_id || data.bankAccounts?.[0]?.id,
          category_id: sourceEstimate?.category_id || undefined,
          calculate_tax: sourceEstimate?.calculate_tax || 'after_discount',
          discount_value: Number(sourceEstimate?.discount || 0),
          discount_type: sourceEstimate?.discount_type || 'percent',
          description: stripHtml(sourceEstimate?.description),
          note: sourceEstimate?.note || '',
        });
        const sourceItems = (sourceEstimate?.items || []).map((item) => {
          let parsedTaxes = [];
          try {
            parsedTaxes = Array.isArray(item.taxes) ? item.taxes : JSON.parse(item.taxes || '[]');
          } catch {
            parsedTaxes = [];
          }
          return {
            item_id: null,
            product_id: item.product_id,
            item_name: item.item_name || '',
            quantity: Number(item.quantity || 1),
            unit_id: item.unit_id,
            cost_per_item: Number(item.unit_price || item.cost_per_item || 0),
            taxes: parsedTaxes.map(Number),
            item_summary: item.item_summary || '',
            ...mapItemDiscountFields(item),
          };
        });
        setInitialItems(sourceItems.length ? sourceItems : [createEmptyItem()]);
      } catch (error) {
        console.error('Error fetching estimate create data:', error);
        message.error('خطا در دریافت اطلاعات');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [cloneEstimateId, presetProjectId, presetClientId, presetDealId]);

  const handleSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      await financeSettingsApi.createEstimate(payload);
      message.success('پیش‌فاکتور با موفقیت ایجاد شد');
      navigate(returnTo || '/finance/estimates');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن پیش‌فاکتور</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ثبت پیش‌فاکتور جدید</p>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={() => navigate(returnTo || '/finance/estimates')}
              style={{ fontFamily: 'CustomFont' }}
            >
              بازگشت به لیست
            </Button>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={6} columns={2} />
            ) : (
              <EstimateForm
                options={options}
                initialValues={initialValues}
                initialItems={initialItems}
                isEdit={false}
                lockClient={lockClient}
                lockProject={lockProject}
                onSubmit={handleSubmit}
                submitLoading={submitLoading}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddEstimate;
